import { useCallback, useEffect, useMemo, useState } from 'react'
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ClipboardList, IndianRupee, TrendingUp, Users } from 'lucide-react'
import { adminApi, unwrap as unwrapPayload } from '../services/api'
import type { ExpressAnalyticsPeriod } from '../services/api'
import { istMonth } from '../utils/dateIST'
import LoadErrorState from '../components/common/LoadErrorState'
import {
  SERVER_ERROR_MESSAGE,
  SERVER_TIMEOUT_MESSAGE,
  withServerTimeout,
} from '../components/common/loadErrorUtils'
import { useColors } from '../hooks/useColors'
import { useToast } from '../contexts/ToastContext'

type RevenuePeriod = '7d' | '30d' | '6m'
type ExpressPeriod = ExpressAnalyticsPeriod

type AnalyticsSummary = {
  total_revenue?: number | string | null
  total_orders?: number | string | null
  avg_order_value?: number | string | null
  total_customers?: number | string | null
  repeat_rate?: number | string | null
}

type BreakdownItem = {
  service_name?: string
  order_count?: number | string
  revenue?: number | string
}

type StatusItem = {
  status?: string
  count?: number | string
}

type AnalyticsResponse = {
  labels?: string[]
  revenue?: Array<number | string>
  orders?: Array<number | string>
  summary?: AnalyticsSummary
  service_breakdown?: BreakdownItem[]
  status_distribution?: StatusItem[]
  monthly_trend?: {
    labels?: string[]
    revenue?: Array<number | string>
    orders?: Array<number | string>
  }
}

type TaxReport = {
  month: string
  total_orders: number
  total_revenue: number
  total_delivery_fees: number
  total_service_revenue: number
  gst_collected: number
  payment_breakdown: {
    online?: { count: number; amount: number }
    cod?: { count: number; amount: number }
    [key: string]: { count: number; amount: number } | undefined
  }
  daily_breakdown: Array<{ date: string | null; orders: number; revenue: number }>
}

type ChartPoint = {
  name: string
  value: number
}

type ExpressAnalyticsPayload = {
  period?: ExpressPeriod
  total_express_orders?: number
  on_time_count?: number
  late_count?: number
  on_time_rate?: number
  avg_delivery_minutes?: number
  fastest_minutes?: number
  slowest_minutes?: number
  revenue?: number
  slot_utilization_by_hour?: Array<{ hour: number; avg_fill_rate: number }>
}

const CARD_SHADOW = '0px 2px 8px rgba(0,0,0,0.04)'

const formatNumber = (value?: number | string | null) =>
  new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 0,
  }).format(Number(value || 0))

const toChartPoints = (labels: string[] = [], values: Array<number | string> = []): ChartPoint[] =>
  labels.map((label, index) => ({
    name: label,
    value: Number(values[index] || 0),
  }))

const chartTooltipStyle = (colors: ReturnType<typeof useColors>) => ({
  background: colors.bgCard,
  border: `1px solid ${colors.border}`,
  borderRadius: '10px',
  boxShadow: '0px 12px 32px rgba(15,46,42,0.12)',
  padding: '10px 14px',
})

function SkeletonCard({ height = 160, colors }: { height?: number; colors: ReturnType<typeof useColors> }) {
  return <div className="rapidry-pulse" style={{ height, borderRadius: '16px', background: colors.surface }} />
}

function LoadingState({ colors }: { colors: ReturnType<typeof useColors> }) {
  return (
    <div>
      <section style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        <SkeletonCard height={156} colors={colors} />
        <SkeletonCard height={156} colors={colors} />
        <SkeletonCard height={156} colors={colors} />
        <SkeletonCard height={156} colors={colors} />
      </section>

      <section style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <SkeletonCard height={400} colors={colors} />
        </div>
      </section>

      <section style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        <div style={{ flex: 1 }}>
          <SkeletonCard height={320} colors={colors} />
        </div>
        <div style={{ flex: 1 }}>
          <SkeletonCard height={320} colors={colors} />
        </div>
      </section>

      <section style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1 }}>
          <SkeletonCard height={320} colors={colors} />
        </div>
        <div style={{ flex: 1 }}>
          <SkeletonCard height={320} colors={colors} />
        </div>
      </section>
    </div>
  )
}

function TrendBadge({ label, background, color }: { label: string; background: string; color: string }) {
  return (
    <span
      className="font-body"
      style={{
        background,
        color,
        borderRadius: '999px',
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  )
}

function ChartTooltip({
  active,
  payload,
  label,
  currency,
  colors,
}: {
  active?: boolean
  payload?: ReadonlyArray<{ value?: unknown }>
  label?: string | number
  currency?: boolean
  colors: ReturnType<typeof useColors>
}) {
  if (!active || !payload || !payload.length) {
    return null
  }

  const rawValue = payload[0]?.value
  const value = Array.isArray(rawValue)
    ? Number(rawValue[0] || 0)
    : Number(rawValue || 0)

  return (
    <div style={chartTooltipStyle(colors)}>
      <div className="font-body" style={{ fontSize: '12px', fontWeight: 400, color: colors.textMuted, marginBottom: '4px' }}>
        {label}
      </div>
      <div className="font-body" style={{ fontSize: '14px', fontWeight: 600, color: colors.textPrimary }}>
        {currency ? `₹${formatNumber(value)}` : formatNumber(value)}
      </div>
    </div>
  )
}

export default function AnalyticsPage() {
  const colors = useColors()
  const { addToast } = useToast()
  const [data, setData] = useState<AnalyticsResponse | null>(null)
  const [taxReport, setTaxReport] = useState<TaxReport | null>(null)
  const [taxMonth, setTaxMonth] = useState(istMonth())
  const [taxLoading, setTaxLoading] = useState(true)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [activeRevenuePeriod, setActiveRevenuePeriod] = useState<RevenuePeriod>('7d')
  const [expressPeriod, setExpressPeriod] = useState<ExpressPeriod>('today')
  const [expressData, setExpressData] = useState<ExpressAnalyticsPayload | null>(null)
  const [expressLoading, setExpressLoading] = useState(true)

  const loadTaxReport = useCallback(async () => {
    setTaxLoading(true)
    try {
      const response = await withServerTimeout(adminApi.getTaxReport({ month: taxMonth, format: 'json' }))
      const payload = unwrapPayload<TaxReport>(response)
      setTaxReport(payload || null)
    } catch (loadError) {
      console.error(loadError)
    } finally {
      setTaxLoading(false)
    }
  }, [taxMonth])

  const loadAnalytics = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await withServerTimeout(adminApi.getAnalytics())
      const payload = unwrapPayload<AnalyticsResponse>(response)
      setData(payload || null)
    } catch (loadError) {
      setError(loadError instanceof Error && loadError.message === SERVER_TIMEOUT_MESSAGE ? SERVER_TIMEOUT_MESSAGE : SERVER_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadExpressAnalytics = useCallback(async (period: ExpressPeriod) => {
    setExpressLoading(true)
    try {
      const response = await withServerTimeout(adminApi.getExpressAnalytics(period))
      const payload = unwrapPayload<ExpressAnalyticsPayload>(response)
      setExpressData(payload || null)
    } catch {
      setExpressData(null)
    } finally {
      setExpressLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAnalytics()
  }, [loadAnalytics])

  useEffect(() => {
    void loadTaxReport()
  }, [loadTaxReport])

  useEffect(() => {
    void loadExpressAnalytics(expressPeriod)
  }, [expressPeriod, loadExpressAnalytics])

  const summary = data?.summary || {}
  const repeatRate = Number(summary.repeat_rate || 0)
  const totalRevenue = Number(summary.total_revenue || 0)
  const totalOrders = Number(summary.total_orders || 0)
  const avgOrderValue = Number(summary.avg_order_value || 0)
  const totalCustomers = Number(summary.total_customers || 0)

  const revenueSeries = useMemo(() => {
    if (activeRevenuePeriod === '6m') {
      return toChartPoints(data?.monthly_trend?.labels || [], data?.monthly_trend?.revenue || [])
    }

    return toChartPoints(data?.labels || [], data?.revenue || [])
  }, [activeRevenuePeriod, data])

  const ordersSeries = useMemo(() => {
    if (activeRevenuePeriod === '6m') {
      return toChartPoints(data?.monthly_trend?.labels || [], data?.monthly_trend?.orders || [])
    }

    return toChartPoints(data?.labels || [], data?.orders || [])
  }, [activeRevenuePeriod, data])

  const maxServiceRevenue = useMemo(() => {
    return (data?.service_breakdown || []).reduce((max, item) => Math.max(max, Number(item.revenue || 0)), 0)
  }, [data])

  const statusData = useMemo(
    () =>
      (data?.status_distribution || []).map((item) => ({
        name: item.status || 'unknown',
        value: Number(item.count || 0),
      })),
    [data],
  )

  const totalStatusCount = useMemo(() => statusData.reduce((sum, item) => sum + item.value, 0), [statusData])

  const statusColorMap: Record<string, string> = {
    placed: '#D6B97B',
    agent_assigned: '#1D4ED8',
    picked_up: '#92400E',
    out_for_delivery: '#92400E',
    at_facility: '#6B21A8',
    in_wash: '#6B21A8',
    in_iron: '#6B21A8',
    quality_check: '#6B21A8',
    ready: '#6B21A8',
    delivered: '#15803D',
    cancelled: '#991B1B',
  }

  // revenueSeries is already computed for the active period (see the memo above).
  const chartData = revenueSeries
  const expressTotalOrders = Number(expressData?.total_express_orders || 0)
  const expressRevenue = Number(expressData?.revenue || 0)
  const standardOrders = Math.max(0, totalOrders - expressTotalOrders)
  const standardRevenue = Math.max(0, totalRevenue - expressRevenue)
  const onTimeRate = Number(expressData?.on_time_rate || 0)
  const avgDeliveryMinutes = Number(expressData?.avg_delivery_minutes || 0)
  // With zero express orders there's no rate to judge - show a neutral state
  // instead of an alarming red 0.0%.
  const hasExpressData = expressTotalOrders > 0
  const onTimeBadgeColor = !hasExpressData ? colors.textMuted : onTimeRate > 90 ? '#15803D' : onTimeRate >= 75 ? '#B45309' : '#B91C1C'
  const onTimeBadgeBg = !hasExpressData ? colors.bgSecondary : onTimeRate > 90 ? '#F0FDF4' : onTimeRate >= 75 ? '#FEF3C7' : '#FEE2E2'

  const modelOrderSplit = useMemo(
    () => [
      { name: 'Express', value: expressTotalOrders },
      { name: 'Standard', value: standardOrders },
    ],
    [expressTotalOrders, standardOrders],
  )

  const modelRevenueSplit = useMemo(
    () => [
      { name: 'Express', value: expressRevenue },
      { name: 'Standard', value: standardRevenue },
    ],
    [expressRevenue, standardRevenue],
  )

  const slotUtilizationSeries = useMemo(
    () =>
      (expressData?.slot_utilization_by_hour || []).map((row) => ({
        name: `${String(row.hour).padStart(2, '0')}:00`,
        value: Math.round(Number(row.avg_fill_rate || 0) * 100),
      })),
    [expressData?.slot_utilization_by_hour],
  )

  const paymentOnline = taxReport?.payment_breakdown?.online || { count: 0, amount: 0 }
  const paymentCod = taxReport?.payment_breakdown?.cod || { count: 0, amount: 0 }

  const dailyRevenueSeries = useMemo(
    () =>
      (taxReport?.daily_breakdown || []).map((row) => ({
        name: row.date ? new Date(row.date).getDate().toString().padStart(2, '0') : '--',
        value: Number(row.revenue || 0),
      })),
    [taxReport?.daily_breakdown],
  )

  const handleDownloadCsv = async () => {
    try {
      const response = await adminApi.getTaxReportCsv(taxMonth)
      const blob = new Blob([response.data], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `rapidry-tax-report-${taxMonth}.csv`
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (downloadError) {
      console.error(downloadError)
      addToast('Unable to download CSV. Please try again.', 'error')
    }
  }

  const handleDownloadPdf = () => {
    if (!taxReport) {
      return
    }

    const reportHtml = `
      <html>
        <head>
          <title>Rapidry Tax Report</title>
          <style>
            body { font-family: 'Arial', sans-serif; padding: 24px; color: ${colors.textPrimary}; }
            h1 { font-size: 22px; margin-bottom: 4px; }
            .muted { color: ${colors.textSecondary}; font-size: 12px; }
            table { width: 100%; border-collapse: collapse; margin-top: 16px; }
            th, td { border-bottom: 1px solid ${colors.border}; padding: 8px; text-align: left; font-size: 12px; }
            .summary { display: flex; gap: 16px; margin-top: 16px; }
            .card { border: 1px solid ${colors.border}; padding: 12px; border-radius: 10px; min-width: 160px; }
          </style>
        </head>
        <body>
          <h1>Rapidry Tax Report</h1>
          <div class="muted">Month: ${taxReport.month}</div>
          <div class="summary">
            <div class="card"><strong>Orders</strong><div>${taxReport.total_orders}</div></div>
            <div class="card"><strong>Revenue</strong><div>Rs ${formatNumber(taxReport.total_revenue)}</div></div>
            <div class="card"><strong>GST</strong><div>Rs ${formatNumber(taxReport.gst_collected)}</div></div>
          </div>
          <table>
            <thead><tr><th>Date</th><th>Orders</th><th>Revenue</th></tr></thead>
            <tbody>
              ${(taxReport.daily_breakdown || [])
                .map((row) => `<tr><td>${row.date || ''}</td><td>${row.orders}</td><td>Rs ${formatNumber(row.revenue)}</td></tr>`)
                .join('')}
            </tbody>
          </table>
        </body>
      </html>
    `

    const pdfWindow = window.open('', '_blank')
    if (!pdfWindow) {
      return
    }
    pdfWindow.document.write(reportHtml)
    pdfWindow.document.close()
    pdfWindow.focus()
    pdfWindow.print()
  }

  if (loading) {
    return <LoadingState colors={colors} />
  }

  if (error) {
    return <LoadErrorState message={error} onRetry={() => void loadAnalytics()} />
  }

  return (
    <div>
      <section style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        {[
          {
            icon: IndianRupee,
            value: `₹${formatNumber(totalRevenue)}`,
            // Value of orders RECEIVED, at their final amount, excluding
            // cancelled work - the same definition the calendar and dashboard
            // use. Not money collected, which is its own figure.
            label: 'All-time Order Value',
            badge: '↑ Order value',
            badgeBg: '#F0FDF4',
            badgeColor: '#15803D',
          },
          {
            icon: ClipboardList,
            value: formatNumber(totalOrders),
            label: 'Total Orders',
            badge: formatNumber(totalOrders),
            badgeBg: '#EFF6FF',
            badgeColor: '#1D4ED8',
          },
          {
            icon: TrendingUp,
            value: `₹${formatNumber(Math.round(avgOrderValue))}`,
            label: 'Avg Order Value',
            badge: 'AOV',
            badgeBg: '#F3E8FF',
            badgeColor: '#6B21A8',
          },
          {
            icon: Users,
            value: formatNumber(totalCustomers),
            label: 'Registered Customers',
            badge: `${repeatRate.toFixed(0)}% repeat`,
            badgeBg: '#FEF3C7',
            badgeColor: '#92400E',
          },
        ].map((card) => (
          <article
            key={card.label}
            style={{
              flex: 1,
              background: colors.bgCard,
              borderRadius: '16px',
              padding: '20px 24px',
              boxShadow: CARD_SHADOW,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '999px', background: colors.surface, display: 'grid', placeItems: 'center' }}>
                <card.icon size={20} color={colors.textPrimary} strokeWidth={2} />
              </div>
              <TrendBadge label={card.badge} background={card.badgeBg} color={card.badgeColor} />
            </div>

            <div className="font-display" style={{ fontSize: '28px', fontWeight: 700, lineHeight: 1, color: colors.textPrimary, marginBottom: '4px' }}>
              {card.value}
            </div>

            <p className="font-body" style={{ margin: 0, fontSize: '13px', fontWeight: 400, color: colors.textSecondary }}>
              {card.label}
            </p>
          </article>
        ))}
      </section>

      <section
        style={{
          background: colors.bgCard,
          borderRadius: '16px',
          padding: '24px',
          boxShadow: CARD_SHADOW,
          marginBottom: '24px',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px' }}>
          <div>
            <h2 className="font-display" style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: colors.textPrimary }}>
              Order Value Trend
            </h2>
            {/* Says which business question this answers. The series is keyed on
                when orders were PLACED (IST), so it agrees with the orders
                calendar day for day; collections are a different chart. */}
            <p className="font-body" style={{ margin: '2px 0 0', fontSize: '12px', color: colors.textMuted }}>
              Value of orders placed, by order date
            </p>
          </div>

          <div style={{ display: 'inline-flex', background: colors.surface, borderRadius: '12px', padding: '3px', gap: '3px' }}>
            {[
              { key: '7d', label: '7 Days' },
              { key: '30d', label: '30 Days' },
              { key: '6m', label: '6 Months' },
            ].map((period) => {
              const isActive = activeRevenuePeriod === period.key

              return (
                <button
                  key={period.key}
                  type="button"
                  className="font-body"
                  onClick={() => setActiveRevenuePeriod(period.key as RevenuePeriod)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '10px',
                    border: 'none',
                    background: isActive ? colors.textPrimary : 'transparent',
                    color: isActive ? colors.bgCard : colors.textSecondary,
                    fontSize: '12px',
                    fontWeight: isActive ? 600 : 400,
                    cursor: 'pointer',
                  }}
                >
                  {period.label}
                </button>
              )
            })}
          </div>
        </div>

        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="goldGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#D6B97B" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#D6B97B" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.surface} vertical={false} />
            <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} />
            <YAxis axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} tickFormatter={(value) => `₹${formatNumber(value)}`} />
            <Tooltip content={(props) => <ChartTooltip {...props} currency colors={colors} />} />
            <Area type="monotone" dataKey="value" stroke="#D6B97B" strokeWidth={2} fill="url(#goldGradient)" />
          </AreaChart>
        </ResponsiveContainer>
      </section>

      <section style={{ display: 'flex', gap: '20px', marginBottom: '24px' }}>
        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW, minWidth: 0 }}>
          <h2 className="font-display" style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Order Volume
          </h2>

          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={ordersSeries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={colors.surface} vertical={false} />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} />
              <Tooltip content={(props) => <ChartTooltip {...props} colors={colors} />} />
              <Bar dataKey="value" fill={colors.textPrimary} radius={[4, 4, 0, 0]} barSize={28} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW, minWidth: 0 }}>
          <h2 className="font-display" style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Top Services
          </h2>

          <div style={{ display: 'grid', gap: '14px' }}>
            {(data?.service_breakdown || []).length === 0 ? (
              <p className="font-body" style={{ margin: 0, fontSize: '14px', color: colors.textMuted }}>
                No service breakdown available.
              </p>
            ) : (
              data?.service_breakdown?.map((service) => {
                const serviceRevenue = Number(service.revenue || 0)
                const widthPercent = maxServiceRevenue > 0 ? (serviceRevenue / maxServiceRevenue) * 100 : 0

                return (
                  <div key={service.service_name} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div className="font-body" style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                      {service.service_name || '-'}
                    </div>
                    <div style={{ width: '120px', height: '8px', borderRadius: '999px', background: colors.surface, overflow: 'hidden', flexShrink: 0 }}>
                      <div style={{ width: `${widthPercent}%`, height: '100%', borderRadius: '999px', background: '#D6B97B' }} />
                    </div>
                    <div className="font-body" style={{ width: '80px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: '#D6B97B', flexShrink: 0 }}>
                      ₹{formatNumber(serviceRevenue)}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </section>

      <section style={{ display: 'flex', gap: '20px' }}>
        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW, minWidth: 0 }}>
          <h2 className="font-display" style={{ margin: '0 0 16px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Order Status Distribution
          </h2>

          <div style={{ position: 'relative', width: '200px', height: '200px', margin: '0 auto' }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  innerRadius={55}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                >
                  {statusData.map((entry) => (
                    <Cell key={entry.name} fill={statusColorMap[entry.name.toLowerCase()] || '#D6B97B'} />
                  ))}
                </Pie>
                <Tooltip content={(props) => <ChartTooltip {...props} colors={colors} />} />
              </PieChart>
            </ResponsiveContainer>

            <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
              <div>
                <div className="font-display" style={{ fontSize: '22px', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                  {formatNumber(totalStatusCount)}
                </div>
                <div className="font-body" style={{ fontSize: '11px', color: colors.textMuted, marginTop: '4px' }}>
                  Orders
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginTop: '16px' }}>
            {statusData.map((entry) => {
              const key = entry.name.toLowerCase()
              const color = statusColorMap[key] || '#D6B97B'

              return (
                <div key={entry.name} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '8px', height: '8px', borderRadius: '999px', background: color }} />
                  <span className="font-body" style={{ fontSize: '12px', fontWeight: 400, color: colors.textSecondary }}>
                    {entry.name.replace(/_/g, ' ')}
                  </span>
                  <span className="font-body" style={{ fontSize: '12px', fontWeight: 600, color: colors.textPrimary }}>
                    {formatNumber(entry.value)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>

        <div style={{ flex: 1, background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW, minWidth: 0 }}>
          <h2 className="font-display" style={{ margin: '0 0 24px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Customer Retention
          </h2>

          <div style={{ display: 'grid', placeItems: 'center' }}>
            <div style={{ position: 'relative', width: '160px', height: '160px' }}>
              <svg width="160" height="160" viewBox="0 0 160 160" style={{ display: 'block' }}>
                <circle cx="80" cy="80" r="65" fill="none" stroke={colors.surface} strokeWidth="10" />
                <circle
                  cx="80"
                  cy="80"
                  r="65"
                  fill="none"
                  stroke="#D6B97B"
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 65}`}
                  strokeDashoffset={`${2 * Math.PI * 65 * (1 - Math.min(Math.max(repeatRate, 0), 100) / 100)}`}
                  transform="rotate(-90 80 80)"
                />
              </svg>
              <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', textAlign: 'center' }}>
                <div>
                  <div className="font-display" style={{ fontSize: '32px', fontWeight: 700, color: colors.textPrimary, lineHeight: 1 }}>
                    {repeatRate.toFixed(0)}%
                  </div>
                  <div className="font-body" style={{ fontSize: '13px', fontWeight: 400, color: colors.textMuted, marginTop: '4px' }}>
                    Repeat Rate
                  </div>
                </div>
              </div>
            </div>
          </div>

          <p className="font-body" style={{ margin: '20px 0 0', fontSize: '14px', fontWeight: 400, color: colors.textSecondary, textAlign: 'center' }}>
            {repeatRate.toFixed(0)}% of customers have ordered more than once
          </p>
        </div>
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr)', gap: '20px', marginTop: '24px' }}>
        <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '18px', gap: '12px' }}>
            <h2 className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              Express Performance
            </h2>
            <div style={{ display: 'inline-flex', background: colors.surface, borderRadius: '12px', padding: '3px', gap: '3px' }}>
              {[
                { key: 'today', label: 'Today' },
                { key: 'week', label: 'This Week' },
                { key: 'month', label: 'This Month' },
              ].map((period) => {
                const isActive = expressPeriod === period.key
                return (
                  <button
                    key={period.key}
                    type="button"
                    className="font-body"
                    onClick={() => setExpressPeriod(period.key as ExpressPeriod)}
                    style={{
                      padding: '6px 10px',
                      borderRadius: '10px',
                      border: 'none',
                      background: isActive ? colors.textPrimary : 'transparent',
                      color: isActive ? colors.bgCard : colors.textSecondary,
                      fontSize: '12px',
                      fontWeight: isActive ? 600 : 400,
                      cursor: 'pointer',
                    }}
                  >
                    {period.label}
                  </button>
                )
              })}
            </div>
          </div>
          {expressLoading ? (
            <div className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>Loading express metrics...</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '12px', marginBottom: '14px' }}>
                <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '12px' }}>
                  <div className="font-body" style={{ color: colors.textMuted, fontSize: '11px' }}>Total Express Orders</div>
                  <div className="font-display" style={{ color: colors.textPrimary, fontSize: '24px', fontWeight: 700 }}>{formatNumber(expressTotalOrders)}</div>
                </div>
                <div style={{ background: onTimeBadgeBg, borderRadius: '12px', padding: '12px' }}>
                  <div className="font-body" style={{ color: colors.textMuted, fontSize: '11px' }}>On-time Delivery Rate</div>
                  {hasExpressData ? (
                    <>
                      <div className="font-display" style={{ color: onTimeBadgeColor, fontSize: '24px', fontWeight: 700 }}>{onTimeRate.toFixed(1)}%</div>
                      <div className="font-body" style={{ color: onTimeBadgeColor, fontSize: '11px' }}>on time</div>
                    </>
                  ) : (
                    <div className="font-body" style={{ color: colors.textMuted, fontSize: '13px', fontWeight: 600, marginTop: '4px' }}>No express orders yet</div>
                  )}
                </div>
                <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '12px' }}>
                  <div className="font-body" style={{ color: colors.textMuted, fontSize: '11px' }}>Avg Delivery Time</div>
                  <div className="font-display" style={{ color: colors.textPrimary, fontSize: '24px', fontWeight: 700 }}>{avgDeliveryMinutes.toFixed(1)} min</div>
                  <div className="font-body" style={{ color: colors.textMuted, fontSize: '11px' }}>40 min target</div>
                </div>
                <div style={{ background: colors.bgSecondary, borderRadius: '12px', padding: '12px' }}>
                  <div className="font-body" style={{ color: colors.textMuted, fontSize: '11px' }}>Fastest / Slowest</div>
                  <div className="font-display" style={{ color: colors.textPrimary, fontSize: '18px', fontWeight: 700 }}>
                    {formatNumber(expressData?.fastest_minutes || 0)} min / {formatNumber(expressData?.slowest_minutes || 0)} min
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW }}>
          <h2 className="font-display" style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Delivery Model Split
          </h2>
          <div style={{ display: 'grid', gap: '16px' }}>
            <div>
              <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px' }}>Order count split</div>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={modelOrderSplit} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70}>
                    <Cell fill="#D6B97B" />
                    <Cell fill={colors.textPrimary} />
                  </Pie>
                  <Tooltip content={(props) => <ChartTooltip {...props} colors={colors} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div>
              <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px' }}>Revenue split</div>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={modelRevenueSplit} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70}>
                    <Cell fill="#D6B97B" />
                    <Cell fill="#1D4ED8" />
                  </Pie>
                  <Tooltip content={(props) => <ChartTooltip {...props} currency colors={colors} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        <div style={{ background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW }}>
          <h2 className="font-display" style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Express Slot Utilization
          </h2>
          <div className="font-body" style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '8px' }}>
            Avg fill rate by hour
          </div>
          {slotUtilizationSeries.length === 0 ? (
            <div className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>No slot data available for this period.</div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={slotUtilizationSeries} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.surface} vertical={false} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} />
                <YAxis axisLine={false} tickLine={false} domain={[0, 100]} tick={{ fontFamily: 'DM Sans', fontSize: 11, fill: colors.textMuted }} tickFormatter={(v) => `${v}%`} />
                <Tooltip content={(props) => <ChartTooltip {...props} colors={colors} />} />
                <Bar dataKey="value" fill="#D6B97B" radius={[4, 4, 0, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      <section style={{ marginTop: '24px', background: colors.bgCard, borderRadius: '16px', padding: '24px', boxShadow: CARD_SHADOW }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <div>
            <h2 className="font-display" style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              Tax Report
            </h2>
            <p className="font-body" style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textMuted }}>
              Monthly GST-ready summary
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="month"
              value={taxMonth}
              onChange={(event) => setTaxMonth(event.target.value)}
              style={{ height: '36px', borderRadius: '10px', border: `1.5px solid ${colors.border}`, padding: '0 10px' }}
            />
            <button
              type="button"
              className="font-body"
              onClick={() => void loadTaxReport()}
              style={{ height: '36px', borderRadius: '999px', border: `1px solid ${colors.textPrimary}`, padding: '0 14px', background: colors.bgCard, fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
            >
              Refresh
            </button>
            <button
              type="button"
              className="font-body"
              onClick={handleDownloadCsv}
              style={{ height: '36px', borderRadius: '999px', border: `1px solid ${colors.accent}`, padding: '0 14px', background: colors.surface, fontSize: '12px', fontWeight: 600, color: '#B45309', cursor: 'pointer' }}
            >
              Download CSV
            </button>
            <button
              type="button"
              className="font-body"
              onClick={handleDownloadPdf}
              style={{ height: '36px', borderRadius: '999px', border: 'none', padding: '0 14px', background: colors.textPrimary, fontSize: '12px', fontWeight: 600, color: colors.bgCard, cursor: 'pointer' }}
            >
              Download PDF
            </button>
          </div>
        </div>

        {taxLoading ? (
          <div className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>Loading tax report...</div>
        ) : taxReport ? (
          <>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {[
                { label: 'Orders', value: formatNumber(taxReport.total_orders) },
                { label: 'Revenue', value: `₹${formatNumber(taxReport.total_revenue)}` },
                { label: 'GST', value: `₹${formatNumber(taxReport.gst_collected)}` },
                { label: 'Online', value: `₹${formatNumber(paymentOnline.amount)} (${paymentOnline.count})` },
                { label: 'COD', value: `₹${formatNumber(paymentCod.amount)} (${paymentCod.count})` },
              ].map((card) => (
                <div key={card.label} style={{ minWidth: '160px', background: colors.bgSecondary, borderRadius: '12px', padding: '12px 14px' }}>
                  <div className="font-body" style={{ fontSize: '11px', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{card.label}</div>
                  <div className="font-display" style={{ fontSize: '18px', fontWeight: 700, color: colors.textPrimary, marginTop: '6px' }}>{card.value}</div>
                </div>
              ))}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: '18px' }}>
              <div style={{ minHeight: '220px' }}>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dailyRevenueSeries} margin={{ top: 10, right: 10, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={colors.surface} vertical={false} />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 10, fill: colors.textMuted }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fontFamily: 'DM Sans', fontSize: 10, fill: colors.textMuted }} />
                    <Tooltip content={(props) => <ChartTooltip {...props} currency colors={colors} />} />
                    <Bar dataKey="value" fill="#D6B97B" radius={[4, 4, 0, 0]} barSize={18} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div>
                <div style={{ maxHeight: '220px', overflowY: 'auto', border: `1px solid ${colors.surface}`, borderRadius: '12px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: colors.bgSecondary }}>
                        {['Date', 'Orders', 'Revenue'].map((label) => (
                          <th key={label} className="font-body" style={{ textAlign: 'left', padding: '8px 10px', fontSize: '11px', color: colors.textMuted }}>
                            {label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {taxReport.daily_breakdown.map((row) => (
                        <tr key={row.date || Math.random().toString(36)}>
                          <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px' }}>{row.date || '-'}</td>
                          <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px' }}>{row.orders}</td>
                          <td className="font-body" style={{ padding: '8px 10px', fontSize: '12px' }}>₹{formatNumber(row.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="font-body" style={{ fontSize: '13px', color: colors.textMuted }}>No tax report data available.</div>
        )}
      </section>
    </div>
  )
}
